/**
 * Task generation from crop schedules
 * Generates actionable task records based on crop growth phases
 *
 * Task timing rules:
 * - sow_indoor: 1 week before germination phase, duration 2 weeks
 * - transplant_indoor: 1 week before seedling phase, duration 2 weeks
 * - transplant_outdoor: 1 week before maturing phase, duration 3 weeks
 * - sow_outdoor: 1 week before seedling phase (direct sow crops), duration 2 weeks
 * - harvest: 1 week before mature phase, duration = entire mature phase
 * - compost: 1 week before decomposition phase, duration = entire decomposition phase
 */

import { parseDuration, formatWeeks } from '~/crop/crop-schedule/utils/parse-duration'

/**
 * Add days to a date
 */
const addDays = (date, days) => {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

/**
 * Subtract weeks from a date
 */
const subtractWeeks = (date, weeks) => {
  return addDays(date, -weeks * 7)
}

/**
 * Find a phase by its type code (first match)
 */
const findPhaseByType = (phases, phaseTypeCode) => {
  if (!phases || !Array.isArray(phases)) {
    return null
  }
  return phases.find(p => p.phase_type_code === phaseTypeCode) || null
}

/**
 * Find the last phase matching a type code
 * Used for cut_back which targets the closing dormant phase (not the opening one)
 */
const findLastPhaseByType = (phases, phaseTypeCode) => {
  if (!phases || !Array.isArray(phases)) {
    return null
  }
  return [...phases].reverse().find(p => p.phase_type_code === phaseTypeCode) || null
}

/**
 * Task type configuration
 * Maps task codes to their generation rules
 */
const TASK_CONFIGS = {
  sow_indoor: {
    phaseType: 'germination',
    offsetWeeks: 1,      // 1 week before phase
    durationWeeks: 2,     // 2 week window
    description: 'sow seeds indoors',
    requiresDirectSow: false
  },
  transplant_indoor: {
    phaseType: 'seedling',
    offsetWeeks: 1,
    durationWeeks: 2,
    description: 'transplant sprouted seeds to more light',
    requiresDirectSow: false
  },
  transplant_outdoor: {
    phaseType: 'maturing',
    offsetWeeks: 1,
    durationWeeks: 3,
    description: 'transplant seedlings to outside plots',
    requiresDirectSow: false
  },
  sow_outdoor: {
    phaseType: 'germination',    // For direct sow crops, sow before seedling phase
    offsetWeeks: 1,
    durationWeeks: 2,
    description: 'sow seeds directly outdoors',
    requiresDirectSow: true   // Only for direct sow crops
  },
  support: {
    phaseType: 'maturing',
    offsetWeeks: -2,
    durationWeeks: 3,
    description: 'secure supports for maturing crops',
    requiresDirectSow: false
  },
  harvest: {
    phaseType: 'mature',
    offsetWeeks: 0,
    usePhaseDuration: true,   // Duration = entire mature phase
    description: 'collect mature crops from the plot',
    requiresDirectSow: null   // Applies to all crops
  },
  compost: {
    phaseType: 'decomposition',
    offsetWeeks: 0,
    durationWeeks: 2,   // Duration = entire decomposition phase
    description: 'collect and process plant biomass after harvest',
    requiresDirectSow: null   // Applies to all crops
  },
  cut_back: {
    phaseType: 'dormant',
    useLastMatch: true,       // Target closing dormant (not opening dormant in subsequent years)
    offsetWeeks: 0,
    durationWeeks: 2,
    description: 'cut back and prepare the crop for winter',
    requiresDirectSow: null   // Applies to all crops
  }
}

/**
 * Check if crop is direct-sown outdoors (not started indoors)
 * Supports both legacy single propagation_code and new M2M propagation_codes array
 */
const isDirectSowCrop = (crop) => {
  const species = crop?.template_crop_species

  // New M2M relationship: check if array includes 'outdoor_sow'
  if (species?.propagation_codes && Array.isArray(species.propagation_codes)) {
    return species.propagation_codes.includes('outdoor_sow')
  }

  // Legacy single propagation_code (backward compatibility)
  return species?.propagation_code === 'outdoor_sow'
}

/**
 * Generate a single task object
 */
const generateTask = (taskCode, config, phase, crop, options) => {
  const phaseStartDate = new Date(phase.est_started_at)
  const taskWindowStart = subtractWeeks(phaseStartDate, config.offsetWeeks)

  // Calculate duration
  let durationDays
  if (config.usePhaseDuration) {
    // Use the phase's duration (for harvest and compost tasks)
    durationDays = parseDuration(phase.est_duration)
    if (durationDays === 0) {
      durationDays = config.durationWeeks * 7 // Fallback
    }
  } else {
    // Use fixed duration from config
    durationDays = config.durationWeeks * 7
  }

  // Generate descriptive notes
  const phaseName = phase.phase_type?.name || config.phaseType
  const notes = `Generated from ${phaseName} phase - ${config.description}`

  return {
    task_code: taskCode,
    organization_id: crop.organization_id,
    crop_id: crop.id,
    state: options.state || 'ready',
    task_window_start: taskWindowStart.toISOString(),
    duration: formatWeeks(durationDays / 7), // Convert back to weeks for consistency
    notes,
    created_by: options.userId
  }
}

/**
 * Generate tasks from schedule data
 *
 * @param {Object} scheduleData - Schedule data object
 * @param {Object} scheduleData.crop - Crop object with template info
 * @param {Object} scheduleData.schedule - Schedule object
 * @param {Array} scheduleData.phases - Array of phase objects
 * @param {Object} options - Generation options
 * @param {string} options.userId - Required: User ID for created_by field
 * @param {boolean} options.includePlotTasks - Include plot-level tasks (default: false)
 * @param {string} options.state - Default task state (default: 'ready')
 * @param {Array<string>} options.skipTaskTypes - Task codes to exclude
 * @returns {Array<Object>} Array of task objects ready for database insertion
 */
export const generateTasksFromSchedule = (scheduleData, options = {}) => {
  // Validate inputs
  if (!scheduleData || !scheduleData.crop || !scheduleData.phases) {
    console.warn('Invalid schedule data provided to generateTasksFromSchedule')
    return []
  }

  if (!options.userId) {
    throw new Error('userId is required in options for task generation')
  }

  const { crop, phases } = scheduleData
  const tasks = []

  // Check if crop is direct-sown
  const isDirectSow = isDirectSowCrop(crop)

  // Generate tasks for each configured task type
  for (const [taskCode, config] of Object.entries(TASK_CONFIGS)) {
    // Skip if in skipTaskTypes list
    if (options.skipTaskTypes?.includes(taskCode)) {
      continue
    }

    // Skip plot tasks unless explicitly included
    if (!options.includePlotTasks && taskCode.startsWith('plot_')) {
      continue
    }

    // Check direct sow requirement
    if (config.requiresDirectSow === true && !isDirectSow) {
      continue // Skip sow_outdoor for non-direct-sow crops
    }
    if (config.requiresDirectSow === false && isDirectSow) {
      // For direct sow crops, skip indoor tasks
      if (taskCode === 'sow_indoor' || taskCode === 'transplant_indoor' || taskCode === 'transplant_outdoor') {
        continue
      }
    }

    // Find the corresponding phase (use last match for tasks that target closing phases)
    const phase = config.useLastMatch
      ? findLastPhaseByType(phases, config.phaseType)
      : findPhaseByType(phases, config.phaseType)

    if (!phase) {
      // Phase doesn't exist in schedule — use fallbackDate if provided, otherwise skip
      if (!options.fallbackDate) continue
      const fallbackPhase = {
        est_started_at: options.fallbackDate instanceof Date
          ? options.fallbackDate.toISOString()
          : options.fallbackDate,
        est_duration: '14 days',
        phase_type: { name: config.phaseType },
      }
      try {
        tasks.push(generateTask(taskCode, { ...config, offsetWeeks: 0 }, fallbackPhase, crop, options))
      } catch (error) {
        console.error(`Error generating task ${taskCode}:`, error)
      }
      continue
    }

    // Generate the task
    try {
      const task = generateTask(taskCode, config, phase, crop, options)
      tasks.push(task)
    } catch (error) {
      console.error(`Error generating task ${taskCode}:`, error)
    }
  }

  return tasks
}
